import { useState, useMemo, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { knowledgeApi } from '../../lib/knowledge-api';
import { getPositions } from '../../constants/graphPositions';
import { DragProvider, useDrag } from './DragContext';
import ItemNode from './ItemNode';
import EmptySlotNode from './EmptySlotNode';
import ConnectionLine from './ConnectionLine';
import DragPhantom from './DragPhantom';
import Dropdown from '../shared/Dropdown';

export default function CreateCollectionModal({ isOpen, onClose, items = [] }) {
  if (!isOpen) return null;

  return (
    <DragProvider>
      <CreateCollectionModalContent isOpen={isOpen} onClose={onClose} items={items} />
    </DragProvider>
  );
}

function CreateCollectionModalContent({ isOpen, onClose, items }) {
  const [isLoading, setIsLoading] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('all');

  // Slots state - 4 slots for the collection
  const [slots, setSlots] = useState([
    { position: 0, item: null },
    { position: 1, item: null },
    { position: 2, item: null },
    { position: 3, item: null },
  ]);

  // Graph dimensions
  const containerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setContainerDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Get unique products from items for dropdown
  const productOptions = useMemo(() => {
    const uniqueProducts = [...new Set(items.map(item => item.product))];
    const options = [
      { value: 'all', label: 'All Products' },
      ...uniqueProducts.sort().map(product => ({ value: product, label: product }))
    ];
    return options;
  }, [items]);

  // Filter items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      // Handle null titles safely
      const title = item.title || '';
      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesProduct = selectedProduct === 'all' || item.product === selectedProduct;
      // Filter out items that are already in slots
      const notInSlots = !slots.some(slot => slot.item?.item_id === item.item_id);
      return matchesSearch && matchesProduct && notInSlots;
    });
  }, [items, searchTerm, selectedProduct, slots]);

  const handleSave = async () => {
    // Get all item IDs from filled slots
    const itemIds = slots.filter(slot => slot.item !== null).map(slot => slot.item.item_id);

    if (itemIds.length === 0) {
      toast.error('Please add at least one item to the collection');
      return;
    }

    setIsLoading(true);

    try {
      // Create the collection via API (title will be auto-generated)
      const response = await knowledgeApi.collections.create({
        item_ids: itemIds,
      });

      // Show success message
      toast.success(response.message || 'Collection created successfully!');

      // Reset form and close modal
      setSlots([
        { position: 0, item: null },
        { position: 1, item: null },
        { position: 2, item: null },
        { position: 3, item: null },
      ]);
      onClose();
    } catch (error) {
      // Error handling is done by the API client interceptor
      console.error('Failed to create collection:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSlots([
      { position: 0, item: null },
      { position: 1, item: null },
      { position: 2, item: null },
      { position: 3, item: null },
    ]);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-7xl h-[700px] flex flex-col" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Create Collection</h3>
                        <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Modal Content - Split View */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Side - Items List */}
            <div className="w-1/3 border-r border-gray-700 flex flex-col">
              {/* Filters */}
              <div className="p-4 border-b border-gray-700 space-y-3">
                <h4 className="text-sm font-semibold text-gray-400 mb-3">Available Items</h4>

                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500"
                  />
                  <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {/* Product Filter */}
                <Dropdown
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  options={productOptions}
                  className="w-full"
                />
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto">
                {filteredItems.length > 0 ? (
                  filteredItems.map((item) => (
                    <DraggableItem key={item.item_id} item={item} slots={slots} setSlots={setSlots} />
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-gray-400">
                      {searchTerm || selectedProduct !== 'all'
                        ? 'No matching items'
                        : 'No items available'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Graph View */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Graph Area */}
              <div className="flex-1 relative overflow-hidden">
                <GraphBuilder
                  containerRef={containerRef}
                  containerDimensions={containerDimensions}
                  slots={slots}
                  setSlots={setSlots}
                />
              </div>

              {/* Fixed Bottom Buttons */}
              <div className="p-6 border-t border-gray-700 flex justify-end items-center gap-3">
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  style={{ color: 'var(--color-text-on-accent)' }}
                >
                  {isLoading && (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>{isLoading ? 'Creating...' : 'Create Collection'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DragPhantom />
    </>
  );
}

// Draggable item component for the left list
function DraggableItem({ item, slots, setSlots }) {
  const { startDrag, updateDragPosition, endDrag, hoveredSlot } = useDrag();

  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // Only left-click

    e.preventDefault();

    startDrag(item, `list-${item.item_id}`, false);

    const handleMouseMove = (e) => {
      updateDragPosition(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      // Check if dropped on a valid slot
      if (hoveredSlot && hoveredSlot.startsWith('slot-')) {
        const slotPosition = parseInt(hoveredSlot.split('-')[1]);
        setSlots(prev => prev.map(slot =>
          slot.position === slotPosition
            ? { ...slot, item }
            : slot
        ));
      }

      endDrag();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      className="w-full p-4 text-left border-b border-gray-700 hover:bg-gray-800 transition-all duration-200 cursor-grab active:cursor-grabbing"
    >
      <div className="flex items-start space-x-2">
        <svg className="text-blue-400 flex-shrink-0 mt-0.5 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div className="flex-1">
          <div className="font-semibold text-white text-sm">{item.title}</div>
          <div className="text-xs text-gray-400 mt-1">{item.product}</div>
        </div>
      </div>
    </div>
  );
}

// Graph builder component for the right side
function GraphBuilder({ containerRef, containerDimensions, slots, setSlots }) {
  const { endDrag, hoveredSlot } = useDrag();

  const handleSlotClick = (slotPosition) => {
    // Clear the slot when clicked
    setSlots(prev => prev.map(slot =>
      slot.position === slotPosition
        ? { ...slot, item: null }
        : slot
    ));
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div ref={containerRef} className="relative w-full h-full max-w-4xl">
        {/* Collection Node - Center */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="relative bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl px-8 py-6 shadow-2xl border-2 border-purple-400 max-w-xs select-none" style={{ color: 'var(--color-text-on-accent)' }}>
                      {/* Collection Label */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gray-800 px-3 py-1 rounded-full border border-purple-400">
              <p className="text-xs text-purple-300 uppercase tracking-wide font-semibold">Collection</p>
            </div>
            <h3 className="text-white font-bold text-base text-center line-clamp-2 min-h-[3rem] flex items-center justify-center">
              Drag items here
            </h3>
          </div>
        </div>

        {/* Slots and Connection Lines */}
        {slots.map((slot) => {
          const position = getPositions.singleViewItem(slot.position);
          const hasItem = slot.item !== null;

          return (
            <div key={slot.position}>
              {/* Connection Line from center to slot */}
              <ConnectionLine
                startX={50}
                startY={50}
                endX={(() => {
                  let endX = position.left ? parseFloat(position.left) : (100 - parseFloat(position.right));
                  const nodeWidth = 160; // w-40 (compact)
                  const offsetX = containerDimensions.width > 0 ? (nodeWidth / 2) / containerDimensions.width * 100 : 0;
                  return position.left ? endX + offsetX : endX - offsetX;
                })()}
                endY={(() => {
                  let endY = position.top ? parseFloat(position.top) : (100 - parseFloat(position.bottom));
                  const nodeHeight = 96; // h-24 (compact)
                  const offsetY = containerDimensions.height > 0 ? (nodeHeight / 2) / containerDimensions.height * 100 : 0;
                  return position.top ? endY + offsetY : endY - offsetY;
                })()}
                isFilled={hasItem}
              />

              {/* Slot Node */}
              <div className="absolute z-20" style={position}>
                {hasItem ? (
                  <div onClick={() => handleSlotClick(slot.position)}>
                    <ItemNode item={slot.item} isCompact={true} slotId={`slot-${slot.position}`} />
                  </div>
                ) : (
                  <EmptySlotNode isCompact={true} slotId={`slot-${slot.position}`} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
